export const GENERIC_STATUS_MESSAGES: Record<number, string> = {
    200: "Thao tác thành công",
    400: "Yêu cầu không hợp lệ",
    401: "Bạn cần đăng nhập lại",
    403: "Bạn không có quyền này",
    404: "Không tìm thấy dữ liệu",
    500: "Hệ thống đang gặp lỗi, vui lòng thử lại sau",
};


const KNOWN_API_MESSAGE_TRANSLATIONS: Array<[RegExp, string]> = [
    [/^these credentials do not match our records\.?$/i, "Email hoặc mật khẩu không đúng."],
    [/^the provided credentials are incorrect\.?$/i, "Email hoặc mật khẩu không đúng."],
    [/^invalid credentials\.?$/i, "Email hoặc mật khẩu không đúng."],
    [/^unauthenticated\.?$/i, "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại."],
    [/^the email has already been taken\.?$/i, "Email này đã được đăng ký."],
    [/^email has already been taken\.?$/i, "Email này đã được đăng ký."],
    [/^the email has already been used\.?$/i, "Email này đã được đăng ký."],
    [/^email already exists\.?$/i, "Email này đã được đăng ký."],
    [/^the email field is required\.?$/i, "Vui lòng nhập email."],
    [/^the email must be a valid email address\.?$/i, "Email không hợp lệ."],
    [/^the password field is required\.?$/i, "Vui lòng nhập mật khẩu."],
    [/^the password confirmation does not match\.?$/i, "Xác nhận mật khẩu không khớp."],
    [/^the password must be at least (\d+) characters\.?$/i, "Mật khẩu phải có ít nhất 8 ký tự."],
    [/^the password field must be at least (\d+) characters\.?$/i, "Mật khẩu phải có ít nhất 8 ký tự."],
    [/^the password field must contain at least one letter\.?$/i, "Mật khẩu phải có ít nhất một chữ cái."],
    [/^the password field must contain at least one uppercase and one lowercase letter\.?$/i, "Mật khẩu phải có chữ hoa và chữ thường."],
    [/^the password field must contain at least one number\.?$/i, "Mật khẩu phải có ít nhất một số."],
    [/^the password field must contain at least one symbol\.?$/i, "Mật khẩu phải có ít nhất một ký tự đặc biệt."],
];

export const translateKnownApiMessage = (message: string): string => {
    const normalized = message.trim();

    for (const [pattern, translation] of KNOWN_API_MESSAGE_TRANSLATIONS) {
        if (pattern.test(normalized)) {
            return translation;
        }
    }

    return normalized;
};

type ResolveOptions = {
    status?: number;
    code?: string | number;
    fallbackMessage?: string;
};

const getNestedValue = (value: unknown, path: string[]): unknown => {
    return path.reduce<unknown>((acc, key) => {
        if (!acc || typeof acc !== "object") {
            return undefined;
        }

        return (acc as Record<string, unknown>)[key];
    }, value);
};

export const getMessageFromPayload = (payload: unknown): string | undefined => {
    if (!payload || typeof payload !== "object") {
        return undefined;
    }

    const candidates = [
        getNestedValue(payload, ["message"]),
        getNestedValue(payload, ["msg"]),
        getNestedValue(payload, ["error"]),
        getNestedValue(payload, ["detail"]),
        getNestedValue(payload, ["description"]),
        getNestedValue(payload, ["data", "message"]),
        getNestedValue(payload, ["errors", "message"]),
        getNestedValue(payload, ["response", "data", "message"]),
        getNestedValue(payload, ["response", "message"]),
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim()) {
            return translateKnownApiMessage(candidate);
        }
    }

    const errors = getNestedValue(payload, ["errors"]);
    if (Array.isArray(errors)) {
        const firstError = errors.find((item) => typeof item === "string" && item.trim());
        if (typeof firstError === "string") {
            return translateKnownApiMessage(firstError);
        }
    }

    if (errors && typeof errors === "object") {
        const firstField = Object.keys(errors as Record<string, unknown>)[0];
        const firstFieldErrors = (errors as Record<string, unknown>)[firstField];

        if (Array.isArray(firstFieldErrors)) {
            const firstMessage = firstFieldErrors.find((item) => typeof item === "string" && item.trim());
            if (typeof firstMessage === "string") {
                return translateKnownApiMessage(firstMessage);
            }
        }

        if (typeof firstFieldErrors === "string" && firstFieldErrors.trim()) {
            return translateKnownApiMessage(firstFieldErrors);
        }
    }

    return undefined;
};

export const getFallbackMessage = ({ status, code, fallbackMessage }: ResolveOptions = {}): string => {
    if (typeof status === "number" && GENERIC_STATUS_MESSAGES[status]) {
        return GENERIC_STATUS_MESSAGES[status];
    }

    if (typeof code === "string" && code.trim()) {
        return `Đã xảy ra lỗi (${code})`;
    }

    if (typeof code === "number") {
        return `Đã xảy ra lỗi (${code})`;
    }

    return fallbackMessage ?? "Đã xảy ra lỗi, vui lòng thử lại";
};

export const resolveApiMessage = (payload: unknown, options: ResolveOptions = {}): string => {
    const resolvedStatus =
        options.status ??
        (typeof getNestedValue(payload, ["status"]) === "number"
            ? (getNestedValue(payload, ["status"]) as number)
            : undefined) ??
        (typeof getNestedValue(payload, ["response", "status"]) === "number"
            ? (getNestedValue(payload, ["response", "status"]) as number)
            : undefined);

    if (resolvedStatus === 403) {
        return GENERIC_STATUS_MESSAGES[403];
    }

    const message = getMessageFromPayload(payload);
    if (message) {
        return message;
    }

    const resolvedCode = options.code ?? getNestedValue(payload, ["code"]) ?? getNestedValue(payload, ["response", "data", "code"]);

    return getFallbackMessage({
        status: resolvedStatus,
        code: typeof resolvedCode === "string" || typeof resolvedCode === "number" ? resolvedCode : undefined,
        fallbackMessage: options.fallbackMessage,
    });
};
